import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  ListChecks, Link2, FileText, Loader2, CheckCircle2, Plus, Trash2, Pencil,
  MessageSquare, Check, AlertCircle, Eye, EyeOff, RefreshCw, Clock, ChevronDown,
  Download,
} from "lucide-react";

/**
 * The client's file checklist — collecting it AND editing it, in one place.
 *
 * Two pages used to share this job. The tracker showed a single line ("Waiting
 * on required files — 3/5 collected") and linked out; a separate Client
 * Checklists page held the list, the answers and every edit. So the moment a
 * PM's job was chasing files, the page built for chasing files sent them
 * somewhere else — and the check-off they actually needed existed on neither.
 *
 * The two modes are deliberately a toggle rather than two panels stacked. They
 * answer different questions and are never wanted at the same time:
 *
 *   RECORD  — "the client sent me this, log it." The common daily job.
 *   EDIT    — "this project doesn't need that, and it needs this instead."
 *             Rare, and it changes what we ASK for rather than what we know.
 *
 * Every checklist is a copy of its template taken at creation, so tailoring one
 * project never touches the template the next project starts from.
 */

/* Types a PM can choose. Mirrors ITEM_TYPES_SELECTABLE in
 * backend/routers/vexos_checklist.py — the retired upload types are absent from
 * both, and the model rejects them, so offering one here would only produce a
 * 422. Named for what a PM is deciding, not for the widget. */
const ITEM_TYPES = [
  { value: "link", label: "Link to files" },
  { value: "input", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "toggle", label: "Yes / no" },
  { value: "select", label: "Pick from a list" },
];

/* The lists a PM can start today. Mirrors CREATABLE_KINDS in
 * backend/routers/vexos_checklist.py. The generic "VEXOS setup" list that came
 * before the VEXOS team's two intakes is gone from both.
 *
 * `kindsToShow` below still appends any kind a project carries that isn't
 * offered here, using the label the backend sends. Nothing live should hit that
 * path now, and it stays because the alternative is hiding a list a client is
 * filling in — which is how the switcher would fail if a kind is ever retired
 * again. */
const KINDS = [
  { key: "vexos_platform", label: "VEXOS platform content", blurb: "The project data that populates the VEXOS platform — specifications, inventory, floor plans, materials, brand assets and sales contacts." },
  { key: "vexos_landing", label: "VEXOS landing page", blurb: "The words and images for the VEXOS landing page — hero, residences, amenities, location and the team." },
  { key: "visualization", label: "Visualization files", blurb: "The source files the renders are built from." },
];

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

export default function ClientFileChecklist({ programId, data, onChanged, readOnly = false }) {
  const [kind, setKind] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("collect");
  const [busyItem, setBusyItem] = useState(null);
  const [recording, setRecording] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSection, setNewSection] = useState({ name: "", description: "" });
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const summaries = data.files?.checklists || [];
  // Offered kinds first, then any retired kind this project actually carries.
  const kindsToShow = [
    ...KINDS,
    ...summaries
      .filter((c) => !KINDS.some((k) => k.key === c.kind))
      .map((c) => ({ key: c.kind, label: c.kind_label || c.kind, blurb: c.kind_blurb || "", retired: true })),
  ];
  const activeKind = kind || summaries[0]?.kind || KINDS[0].key;
  const kindDef = kindsToShow.find((k) => k.key === activeKind) || KINDS[0];
  const summaryFor = (k) => summaries.find((c) => c.kind === k);

  /* "Not there" and "couldn't read it" are different answers, and conflating
   * them is what produced an unescapable page: a list that existed but failed
   * to load looked exactly like no list at all, so the panel offered to create
   * one — and the create refused, because the list it could not open already
   * existed. A read that fails says so, and offers a retry instead of a button
   * that cannot work. */
  const load = useCallback(async () => {
    try {
      const one = await apiClient.get(`/vexos-checklist/project/${programId}`, {
        params: { kind: activeKind },
      });
      // A 200 with no body is the real "no checklist of this kind yet" — the
      // create CTA is the right answer to that one, and not an error.
      if (!one.data?.id) {
        setChecklist(null);
        setLoadError(null);
        return;
      }
      const full = await apiClient.get(`/vexos-checklist/${one.data.id}`);
      setChecklist(full.data);
      setLoadError(null);
    } catch (e) {
      setChecklist(null);
      setLoadError(e.response?.data?.detail || "We couldn't open this list.");
    } finally {
      setLoading(false);
    }
  }, [programId, activeKind]);

  useEffect(() => { load(); }, [load]);

  // Anything that can change a required item's status also moves the project
  // clock, so the tracker's own summary has to be refreshed alongside.
  const reload = async () => { await load(); await onChanged(); };

  /* The client's answers as a Word document — what the VEXOS team works from
   * when they build the platform. The server names the file; the name built
   * here is the fallback for when the header can't be read (cross-origin in
   * local dev, where Content-Disposition isn't exposed). */
  const exportWord = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get(`/vexos-checklist/${checklist.id}/export.docx`, {
        responseType: "blob",
      });
      const header = res.headers?.["content-disposition"] || "";
      const star = header.match(/filename\*=UTF-8''([^;]+)/i);
      const plain = header.match(/filename="([^"]+)"/i);
      const fallback = `${data.program_name || "Project"} - ${kindDef.label}.docx`.replace(/[\\/:*?"<>|]+/g, " ");
      const name = star ? decodeURIComponent(star[1]) : plain ? plain[1] : fallback;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("The Word document couldn't be made. Try again in a moment.");
    } finally {
      setExporting(false);
    }
  };

  const act = async (fn, okMsg) => {
    try {
      await fn();
      await reload();
      if (okMsg) toast.success(okMsg);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "That didn't save");
      return false;
    }
  };

  const createChecklist = async () => {
    setCreating(true);
    try {
      await apiClient.post("/vexos-checklist/create", {
        project_id: programId,
        project_name: data.program_name,
        client_name: data.client_name,
        kind: activeKind,
      });
      await reload();
      toast.success(`${kindDef.label} checklist created from the standard template`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create that checklist");
    } finally {
      setCreating(false);
    }
  };

  const answer = async (item, payload) => {
    setBusyItem(item.id);
    const ok = await act(
      () => apiClient.post(`/vexos-checklist/${checklist.id}/items/${item.id}/answer`, payload),
    );
    if (ok) setRecording(null);
    setBusyItem(null);
  };

  const addSection = async () => {
    if (!newSection.name.trim()) { toast.error("A section needs a name"); return; }
    const ok = await act(() => apiClient.post(`/vexos-checklist/${checklist.id}/sections`, {
      name: newSection.name.trim(),
      description: newSection.description.trim() || null,
      order_index: (checklist.sections || []).length,
    }), "Section added");
    if (ok) { setNewSection({ name: "", description: "" }); setShowAddSection(false); }
  };

  const ops = {
    editSection: (sectionId, patch) => act(
      () => apiClient.patch(`/vexos-checklist/${checklist.id}/sections/${sectionId}`, patch),
      "Section updated",
    ),
    deleteSection: (sectionId) => {
      if (!window.confirm("Delete this section and every item in it?")) return Promise.resolve(false);
      return act(
        () => apiClient.delete(`/vexos-checklist/${checklist.id}/sections/${sectionId}`),
        "Section deleted",
      );
    },
    addItem: (sectionId, item) => act(
      () => apiClient.post(`/vexos-checklist/${checklist.id}/sections/${sectionId}/items`, item),
      "Item added",
    ),
    editItem: (itemId, patch) => act(
      () => apiClient.patch(`/vexos-checklist/${checklist.id}/items/${itemId}`, patch),
      "Item updated",
    ),
    deleteItem: (itemId) => {
      if (!window.confirm("Delete this item? Anything recorded against it goes too.")) {
        return Promise.resolve(false);
      }
      return act(() => apiClient.delete(`/vexos-checklist/${checklist.id}/items/${itemId}`), "Item deleted");
    },
    addRequest: (sectionId, message) => act(
      () => apiClient.post(`/vexos-checklist/${checklist.id}/sections/${sectionId}/request`, {
        section_id: sectionId, message,
      }),
      "Note added — the client sees it on this section",
    ),
    resolveRequest: (requestId) => act(
      () => apiClient.patch(`/vexos-checklist/${checklist.id}/requests/${requestId}/resolve`),
      "Note cleared",
    ),
  };

  const switcher = (
    <div className="flex flex-wrap gap-1.5" data-testid="checklist-kind-switcher">
      {kindsToShow.map((k) => {
        const s = summaryFor(k.key);
        const on = k.key === activeKind;
        return (
          <Button key={k.key} size="sm" variant={on ? "default" : "outline"}
            className="rounded-full text-xs h-7"
            data-testid={`kind-tab-${k.key}`}
            onClick={() => { if (!on) { setKind(k.key); setLoading(true); setRecording(null); } }}>
            {k.label}
            <span className="ml-1.5 opacity-70 tabular-nums">
              {s ? `${s.required_complete}/${s.required_total}` : "not created"}
            </span>
          </Button>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ListChecks className="w-4 h-4" /> Files we need from the client</CardTitle></CardHeader>
        <CardContent><p className="text-xs text-muted-foreground">Loading the list…</p></CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ListChecks className="w-4 h-4" /> Files we need from the client</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {switcher}
          <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-6 text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-3 text-amber-500" />
            <p className="text-sm font-medium">This {kindDef.label} list wouldn't open</p>
            {/* The server's own words, kept on their own line: they rarely end in a
                full stop, and run into the next sentence when inlined. */}
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{loadError}</p>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              It hasn't been lost — nothing on it has changed, and creating a second one isn't
              the answer. Try again, and tell an engineer if it keeps happening.
            </p>
            <Button variant="outline" className="mt-4 rounded-full"
              data-testid={`retry-checklist-${activeKind}`}
              onClick={() => { setLoadError(null); setLoading(true); load(); }}>
              <RefreshCw className="w-4 h-4 mr-1.5" /> Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Both kinds are always offered, whether or not they exist — that is how a PM
  // discovers the visualization list is missing and can create it.
  if (!checklist) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ListChecks className="w-4 h-4" /> Files we need from the client</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {switcher}
          <div className="rounded-xl border border-dashed p-6 text-center">
            <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm font-medium">No {kindDef.label} list on this project</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              {kindDef.blurb} Creating it starts from the standard template — add, edit and remove
              anything afterwards without touching what the next project starts from.
            </p>
            {/* A retired kind has no template left to build from, so the button
                would only ever produce a 422. It can't normally be reached —
                retired kinds appear only where one already exists — but the
                empty state must not offer something the server will refuse. */}
            {!kindDef.retired && !readOnly && (
              <Button className="mt-4 rounded-full" onClick={createChecklist} disabled={creating}
                data-testid={`create-checklist-${activeKind}`}>
                {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                Create the {kindDef.label} list
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // A view-only reader (Sales) can switch kinds and export, nothing else.
  const editing = mode === "edit" && !readOnly;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ListChecks className="w-4 h-4" /> Files we need from the client
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {editing
            ? "Changing what this project asks for. Every list is a copy of its template, so nothing you do here affects the next project."
            : "The same list the client sees. They paste links to the folders their files already live in — Dropbox, Drive, WeTransfer. If they send you something directly instead, record it here and their page will show that you logged it for them."}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Two genuinely different lists — VEXOS collects platform and
              sales-ops setup, visualization collects the source files a render
              is built from. One at a time: stacked, they read as a single long
              list that happens to start with VEXOS. */}
          {switcher}
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" className="rounded-full text-xs h-7"
              onClick={exportWord} disabled={exporting}
              title="Download the client's answers as a Word document"
              data-testid={`export-word-${activeKind}`}>
              {exporting
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Download className="w-3.5 h-3.5 mr-1.5" />}
              Export to Word
            </Button>
            {!readOnly && <div className="flex rounded-full border p-0.5">
              {[["collect", "Record"], ["edit", "Edit the list"]].map(([m, label]) => (
                <button key={m} onClick={() => { setMode(m); setRecording(null); }}
                  data-testid={`checklist-mode-${m}`}
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${
                    mode === m ? "bg-foreground text-background" : "text-muted-foreground"
                  }`}>
                  {label}
                </button>
              ))}
            </div>}
          </div>
        </div>

        {/* <fieldset disabled> switches off every Record / answer control in the
            rows, so a view-only reader sees the client's answers and status
            without a button the server would refuse. */}
        <fieldset disabled={readOnly} className="min-w-0 border-0 p-0 m-0 space-y-4">
        {(checklist.sections || []).map((section) => (
          <ChecklistSection
            key={section.id}
            section={section}
            editing={editing}
            busyItem={busyItem}
            recording={recording}
            setRecording={setRecording}
            onAnswer={answer}
            ops={ops}
          />
        ))}
        </fieldset>

        {editing && (
          showAddSection ? (
            <div className="rounded-xl border border-dashed p-4 space-y-2">
              <div className="grid sm:grid-cols-2 gap-2">
                <Input value={newSection.name} placeholder="Section name — e.g. Marketing materials"
                  onChange={(e) => setNewSection({ ...newSection, name: e.target.value })} />
                <Input value={newSection.description} placeholder="One line for the client (optional)"
                  onChange={(e) => setNewSection({ ...newSection, description: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={addSection}>Add section</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddSection(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" className="w-full border-dashed rounded-xl"
              onClick={() => setShowAddSection(true)}>
              <Plus className="w-4 h-4 mr-1.5" /> Add a section
            </Button>
          )
        )}

        {!readOnly && (
          <LinkAndHistory
            checklist={checklist}
            open={showLinkPanel}
            setOpen={setShowLinkPanel}
            onChanged={reload}
          />
        )}
      </CardContent>
    </Card>
  );
}

/* ── One section, in whichever mode the card is in ─────────────────────────── */

function ChecklistSection({ section, editing, busyItem, recording, setRecording, onAnswer, ops }) {
  const [editingHeader, setEditingHeader] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "" });
  const [addingItem, setAddingItem] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [note, setNote] = useState("");

  const items = section.items || [];
  const clientItems = items.filter((i) => i.visible_in_client_view);
  const outstanding = clientItems.filter(
    (i) => i.required && i.status !== "complete" && i.status !== "n_a",
  ).length;
  const done = clientItems.filter((i) => i.status === "complete" || i.status === "n_a").length;
  const requests = section.client_requests || [];

  // In edit mode a PM is managing the whole list, including items they have
  // hidden from the client — which are invisible in Record mode by design.
  const shown = editing ? items : clientItems;
  if (!editing && !clientItems.length) return null;

  const saveHeader = async () => {
    if (!draft.name.trim()) { toast.error("A section needs a name"); return; }
    const ok = await ops.editSection(section.id, {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
    });
    if (ok) setEditingHeader(false);
  };

  return (
    <div className="rounded-xl border">
      <div className="flex items-start justify-between gap-3 px-4 py-2.5 border-b bg-muted/30">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{section.name}</p>
          {section.description && (
            <p className="text-xs text-muted-foreground mt-0.5">{section.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-xs text-muted-foreground">
            {outstanding ? `${outstanding} required outstanding` : `${done}/${clientItems.length} done`}
          </span>
          {editing && (
            <>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Rename this section"
                onClick={() => {
                  setDraft({ name: section.name || "", description: section.description || "" });
                  setEditingHeader(!editingHeader);
                }}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Add a note for the client"
                onClick={() => setAddingNote(!addingNote)}>
                <MessageSquare className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500"
                title="Delete this section"
                onClick={() => ops.deleteSection(section.id)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {editingHeader && (
        <div className="px-4 py-3 border-b bg-muted/20 space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Section name" />
            <Input value={draft.description} placeholder="One line for the client (optional)"
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveHeader}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingHeader(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Notes the client sees on this section — shown in both modes, because a
          note that has been answered should be cleared by whoever noticed. */}
      {requests.length > 0 && (
        <div className="px-4 py-3 border-b space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-amber-900">{r.message}</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Asked by {r.created_by_name || "the team"} — the client sees this on their list
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs shrink-0"
                onClick={() => ops.resolveRequest(r.id)}>
                <Check className="w-3 h-3 mr-1" /> Clear
              </Button>
            </div>
          ))}
        </div>
      )}

      {addingNote && (
        <div className="px-4 py-3 border-b bg-muted/20 space-y-2">
          <Label className="text-xs">A note for the client on this section</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. We still need the plans for levels 10–18" />
          <div className="flex gap-2">
            <Button size="sm" disabled={!note.trim()}
              onClick={async () => {
                const ok = await ops.addRequest(section.id, note.trim());
                if (ok) { setNote(""); setAddingNote(false); }
              }}>
              Add note
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAddingNote(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* The same screenshots the client is looking at on their own list, so a
          PM walking someone through "the hero section" is describing the thing
          in front of both of them. Opens full size in a tab rather than
          carrying a lightbox into an internal panel. */}
      {(section.reference_images || []).length > 0 && (
        <div className="px-4 py-3 border-b">
          <p className="text-xs text-muted-foreground mb-2">
            What this section looks like on a delivered page
          </p>
          <div className="flex flex-wrap gap-2">
            {section.reference_images.map((shot) => (
              <a key={shot.src} href={shot.src} target="_blank" rel="noopener noreferrer"
                title={shot.caption}
                className="block rounded-md overflow-hidden border hover:opacity-80 transition-opacity">
                <img src={shot.src} alt={shot.caption} loading="lazy"
                  width={shot.width} height={shot.height}
                  className="block h-20 w-auto" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="divide-y">
        {shown.map((item) => (
          editing ? (
            <EditableItem key={item.id} item={item} ops={ops} />
          ) : (
            <ChecklistRow
              key={item.id}
              item={item}
              busy={busyItem === item.id}
              recording={recording === item.id}
              onOpen={() => setRecording(recording === item.id ? null : item.id)}
              onAnswer={(payload) => onAnswer(item, payload)}
            />
          )
        ))}
        {shown.length === 0 && (
          <p className="px-4 py-4 text-xs text-muted-foreground text-center">Nothing in this section.</p>
        )}
      </div>

      {editing && (
        addingItem ? (
          <div className="px-4 py-3 border-t bg-muted/20">
            <ItemForm
              onCancel={() => setAddingItem(false)}
              onSave={async (payload) => {
                const ok = await ops.addItem(section.id, payload);
                if (ok) setAddingItem(false);
              }}
              submitLabel="Add item"
            />
          </div>
        ) : (
          <button onClick={() => setAddingItem(true)}
            className="w-full px-4 py-2.5 border-t text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add an item to {section.name}
          </button>
        )
      )}
    </div>
  );
}

/* ── Edit mode: one item's definition ─────────────────────────────────────── */

function EditableItem({ item, ops }) {
  const [open, setOpen] = useState(false);
  const answered = item.status === "complete" || item.status === "n_a";

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm">{item.label}</span>
            {item.required && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Required</Badge>}
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {(ITEM_TYPES.find((t) => t.value === item.type) || {}).label || item.type}
            </Badge>
            {!item.visible_in_client_view && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">Hidden from client</Badge>
            )}
          </div>
          {item.instructions_client && (
            <p className="text-xs text-muted-foreground mt-0.5">{item.instructions_client}</p>
          )}
          {answered && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Already answered — editing the question keeps the answer.
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Edit this item"
            onClick={() => setOpen(!open)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" title="Delete this item"
            onClick={() => ops.deleteItem(item.id)}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-3 rounded-lg bg-muted/40 p-3">
          <ItemForm
            item={item}
            onCancel={() => setOpen(false)}
            onSave={async (payload) => {
              const ok = await ops.editItem(item.id, payload);
              if (ok) setOpen(false);
            }}
            submitLabel="Save item"
          />
        </div>
      )}
    </div>
  );
}

/* Add and edit share this form: the fields are identical, and two copies drift.
 * `required` is called out on screen because it is the only field here that
 * changes whether the project's clock can start. */
function ItemForm({ item, onCancel, onSave, submitLabel }) {
  const [label, setLabel] = useState(item?.label || "");
  const [type, setType] = useState(item?.type || "link");
  const [required, setRequired] = useState(!!item?.required);
  const [instructions, setInstructions] = useState(item?.instructions_client || "");
  const [visible, setVisible] = useState(item ? item.visible_in_client_view !== false : true);
  const [options, setOptions] = useState((item?.options || []).join(", "));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!label.trim()) { toast.error("An item needs a label"); return; }
    // A dropdown with nothing to pick from is a dead end for the client.
    const opts = options.split(",").map((o) => o.trim()).filter(Boolean);
    if (type === "select" && opts.length < 2) {
      toast.error("A pick-from-a-list item needs at least two choices, comma separated");
      return;
    }
    setSaving(true);
    await onSave({
      label: label.trim(),
      type,
      required,
      instructions_client: instructions.trim() || null,
      visible_in_client_view: visible,
      options: type === "select" ? opts : null,
    });
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">What are we asking for?</Label>
          <Input className="mt-1" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Landscape plans" />
        </div>
        <div>
          <Label className="text-xs">How does it arrive?</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ITEM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {type === "select" && (
        <div>
          <Label className="text-xs">The choices, comma separated</Label>
          <Input className="mt-1" value={options} onChange={(e) => setOptions(e.target.value)}
            placeholder="TRP full service, Client providing, Hybrid" />
        </div>
      )}

      <div>
        <Label className="text-xs">A line of instruction for the client (optional)</Label>
        <Input className="mt-1" value={instructions} onChange={(e) => setInstructions(e.target.value)}
          placeholder="e.g. .dwg or .dxf preferred" />
      </div>

      <div className="flex flex-wrap gap-5">
        <label className="flex items-center gap-2.5 text-xs cursor-pointer">
          <Switch checked={required} onCheckedChange={setRequired} />
          <span>
            <span className="block">Required</span>
            <span className="text-muted-foreground">Required items are what hold the project clock</span>
          </span>
        </label>
        <label className="flex items-center gap-2.5 text-xs cursor-pointer">
          <Switch checked={visible} onCheckedChange={setVisible} />
          <span>
            <span className="block">Show on the client's list</span>
            <span className="text-muted-foreground">Off keeps it internal</span>
          </span>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={saving}>
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}{submitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
      {item && (
        <p className="text-xs text-muted-foreground">
          Anything the client already sent against this item is kept.
        </p>
      )}
    </div>
  );
}

/* When a link came in. "I sent that two weeks ago" is only answerable if the
   date is on the row — manager meeting, 2026-11-02. */
function submittedText(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric", minute: "2-digit",
  });
}

/* ── Record mode: one row of the list ─────────────────────────────────────── */

function ChecklistRow({ item, busy, recording, onOpen, onAnswer }) {
  const links = item.value_json?.links || [];
  const files = item.value_json?.files || [];
  const done = item.status === "complete" || item.status === "n_a";
  const onBehalf = !!item.answered_on_behalf_of_client;

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="shrink-0 mt-1">
          {done
            ? <CheckCircle2 className={`w-4 h-4 ${item.status === "n_a" ? "text-muted-foreground" : "text-green-600"}`} />
            : <span className={`block w-2 h-2 rounded-full mt-1 ${item.required ? "bg-amber-500" : "bg-muted-foreground/30"}`} />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            {item.label}
            {item.required && !done && <span className="text-amber-700 text-xs ml-2">required</span>}
          </p>

          {links.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {links.map((l) => (
                <a key={l.id} href={l.url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline min-w-0">
                  <Link2 className="w-3 h-3 shrink-0" />
                  <span className="truncate">{l.label || l.url}</span>
                  {l.note && <span className="text-muted-foreground shrink-0">· {l.note}</span>}
                  {l.added_at && (
                    <span className="text-muted-foreground shrink-0 tabular-nums">
                      · {l.added_by === "client" ? "client" : "logged"} {submittedText(l.added_at)}
                    </span>
                  )}
                </a>
              ))}
            </div>
          )}

          {files.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {files.map((f) => (
                <a key={f.id} href={f.url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline min-w-0">
                  <FileText className="w-3 h-3 shrink-0" />
                  <span className="truncate">{f.name}</span>
                </a>
              ))}
            </div>
          )}

          {item.value_json?.value !== undefined && item.value_json?.value !== null
            && item.value_json.value !== "" && !links.length && (
            <p className="text-xs text-muted-foreground mt-1">
              {typeof item.value_json.value === "boolean"
                ? (item.value_json.value ? "Yes" : "No")
                : String(item.value_json.value)}
            </p>
          )}

          {item.status === "n_a" && (
            <p className="text-xs text-muted-foreground mt-1">
              Not applicable{item.na_reason ? ` — ${item.na_reason}` : ""}
            </p>
          )}

          {/* The audit line. Who recorded this, and whether the client did it
              themselves, is the whole reason a second door was safe to add. */}
          {onBehalf && item.answered_by_name && (
            <p className="text-xs text-muted-foreground mt-1">
              Recorded by {item.answered_by_name} on the client's behalf
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onOpen}>
            {recording ? "Cancel" : done ? "Change" : "Record"}
          </Button>
        </div>
      </div>

      {recording && <RowRecorder item={item} busy={busy} onAnswer={onAnswer} />}
    </div>
  );
}

/* The PM's entry form for one answer. Three shapes, because there are three
 * real situations: they sent a link, they told you an answer, or it will never
 * apply to this project. */
function RowRecorder({ item, busy, onAnswer }) {
  const existing = item.value_json?.links || [];
  const isLink = item.type === "link";
  const [mode, setMode] = useState(isLink ? "link" : "value");
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [value, setValue] = useState(
    item.value_json?.value === undefined || item.value_json?.value === null
      ? "" : String(item.value_json.value),
  );
  const [naReason, setNaReason] = useState(item.na_reason || "");

  const submit = () => {
    if (mode === "n_a") {
      if (!naReason.trim()) return;
      onAnswer({ status: "n_a", na_reason: naReason.trim() });
      return;
    }
    if (mode === "link") {
      if (!url.trim()) return;
      onAnswer({
        links: [
          ...existing.map((l) => ({ url: l.url, label: l.label, note: l.note })),
          { url: url.trim(), label: label.trim() || null },
        ],
      });
      return;
    }
    onAnswer({ value: item.type === "toggle" ? value === "true" : value.trim() });
  };

  return (
    <div className="mt-3 ml-7 rounded-lg bg-muted/40 p-3 space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {[
          isLink && ["link", "They sent a link"],
          !isLink && ["value", "Record their answer"],
          ["n_a", "Doesn't apply to this project"],
        ].filter(Boolean).map(([m, l]) => (
          <button key={m} type="button" onClick={() => setMode(m)}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              mode === m ? "bg-foreground text-background border-foreground" : "text-muted-foreground"
            }`}>
            {l}
          </button>
        ))}
      </div>

      {mode === "link" && (
        <div className="grid sm:grid-cols-2 gap-2">
          <Input value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste the link they sent (https://…)" />
          <Input value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="What's in it? (optional)" />
        </div>
      )}

      {mode === "value" && (
        item.type === "toggle" ? (
          <div className="flex gap-2">
            {[["true", "Yes"], ["false", "No"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setValue(v)}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  value === v ? "bg-foreground text-background border-foreground" : "text-muted-foreground"
                }`}>{l}</button>
            ))}
          </div>
        ) : item.type === "select" ? (
          <select value={value} onChange={(e) => setValue(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm">
            <option value="">Select</option>
            {(item.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <Input value={value} onChange={(e) => setValue(e.target.value)}
            placeholder="What they told you" />
        )
      )}

      {mode === "n_a" && (
        <div>
          <Input value={naReason} maxLength={280} onChange={(e) => setNaReason(e.target.value)}
            placeholder="Why doesn't it apply? e.g. no landscape package on this project" />
          <p className="text-xs text-muted-foreground mt-1.5">
            Required. This is the record of why the clock started without it.
          </p>
        </div>
      )}

      <Button size="sm" disabled={busy || (mode === "n_a" ? !naReason.trim() : mode === "link" && !url.trim())}
        onClick={submit}>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
        {mode === "n_a" ? "Mark not applicable" : "Record for the client"}
      </Button>
    </div>
  );
}

/* ── The list's own access and its history ────────────────────────────────── */
/*
 * Collapsed by default: none of it is part of the daily job, and it was a
 * permanent bar plus a second tab on the page this replaced.
 *
 * The client reaches this list through the project dashboard link, not through
 * one of its own any more — but the checklist's `client_link_enabled` flag
 * still gates the endpoints that page calls, so switching it off breaks their
 * to-do list. That is worth saying in those words rather than leaving a PM to
 * find out.
 */
function LinkAndHistory({ checklist, open, setOpen, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState("");
  const settings = checklist.client_link_settings || {};
  const activity = checklist.activity_log || [];

  const call = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      await onChanged();
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "That didn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border">
      <button onClick={() => setOpen(!open)}
        data-testid="checklist-access-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left">
        <span className="text-sm flex items-center gap-2">
          {checklist.client_link_enabled
            ? <Link2 className="w-3.5 h-3.5 text-green-600" />
            : <EyeOff className="w-3.5 h-3.5 text-amber-600" />}
          Access and history
          <span className="text-xs text-muted-foreground">
            {checklist.client_link_enabled ? "list is open to the client" : "list is closed to the client"}
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t">
          {!checklist.client_link_enabled && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                This list is switched off, so the client's dashboard can't show it — their to-do
                page will be empty. You can still record answers for them here.
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full text-xs h-8" disabled={busy}
              onClick={() => call(
                () => apiClient.patch(`/vexos-checklist/${checklist.id}`, {
                  client_link_enabled: !checklist.client_link_enabled,
                }),
                checklist.client_link_enabled ? "List closed to the client" : "List open to the client",
              )}>
              {checklist.client_link_enabled
                ? <><EyeOff className="w-3.5 h-3.5 mr-1.5" /> Close to the client</>
                : <><Eye className="w-3.5 h-3.5 mr-1.5" /> Open to the client</>}
            </Button>
            <Button variant="outline" size="sm" className="rounded-full text-xs h-8" disabled={busy}
              onClick={() => {
                if (!window.confirm("Issue a fresh list? Their dashboard picks it up automatically.")) return;
                call(
                  () => apiClient.post(`/vexos-checklist/${checklist.id}/regenerate-token`),
                  "Fresh list issued",
                );
              }}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reissue
            </Button>
          </div>

          <div className="rounded-lg border divide-y">
            <div className="p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm">Let the client fill it in</p>
                <p className="text-xs text-muted-foreground">
                  Off makes their list read-only — useful once everything is in.
                </p>
              </div>
              <Switch className="shrink-0 mt-0.5" disabled={busy}
                checked={settings.can_edit !== false}
                onCheckedChange={(v) => call(
                  () => apiClient.patch(`/vexos-checklist/${checklist.id}/link-settings`, {
                    can_edit: v, can_upload: false,
                  }),
                  v ? "The client can fill it in" : "Their list is now read-only",
                )} />
            </div>
            <div className="p-3 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm">Expire the list</p>
                <p className="text-xs text-muted-foreground">
                  {settings.expires_at
                    ? `Expires ${formatDateTime(settings.expires_at)}`
                    : "Never expires. Leave it that way unless there's a reason."}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Input type="number" min={1} max={365} className="w-20" value={expiresInDays}
                  placeholder="days" onChange={(e) => setExpiresInDays(e.target.value)} />
                <Button variant="outline" size="sm" className="text-xs h-9" disabled={busy || !expiresInDays}
                  onClick={() => call(
                    () => apiClient.patch(`/vexos-checklist/${checklist.id}/link-settings`, {
                      can_edit: settings.can_edit !== false,
                      can_upload: false,
                      expires_in_days: Number(expiresInDays),
                    }),
                    `Expires in ${expiresInDays} days`,
                  )}>
                  Set
                </Button>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Recent activity on this list
            </p>
            {activity.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nothing yet.</p>
            ) : (
              <div className="space-y-2">
                {activity.slice(0, 12).map((log, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                      log.actor === "client" ? "bg-green-600" : "bg-muted-foreground/40"
                    }`} />
                    <div className="min-w-0">
                      <p>
                        <span className="font-medium">
                          {log.actor === "client" ? "The client" : log.actor_name || "Someone internal"}
                        </span>{" "}
                        {(log.action || "").replace(/_/g, " ")}
                      </p>
                      {log.details && <p className="text-muted-foreground">{log.details}</p>}
                      <p className="text-muted-foreground">{formatDateTime(log.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
